import styles from './ProfilePage.module.css';
import ProfileHeader from './ProfileHeader/ProfileHeader.tsx';
import BalanceTop from '../../components/BalanceTop/BalanceTop.tsx';
import ProfileMenu from './ProfileMenu/ProfileMenu.tsx';
import HaveInventory from './HaveInventory/HaveInventory.tsx';
import { useEffect, useState } from 'react';
import { getProfile, withdrawGift } from '../../Requests/profile/profile.ts';
import type { WebApp } from '../../types/telegram';
import CustomLoader from '../../components/CustomLoader/CustomLoader';
import { useNotificationStore } from '../../store/NotificationStore.ts';
import Inventory from './Inventory/Inventory.tsx';
import UserInfoTop from '../../components/UserInfoTop/UserInfoTop.tsx';
import {ReactComponent as StarIcon} from "../../assets/Inventory/stars.svg"
import {ReactComponent as VectorIcon} from "../../assets/Inventory/vector.svg"
import { useModalStore } from '../../store/ModalStore.ts';
import GiftModal from './GiftModal/GiftModal.tsx';

interface ExtendedWebApp extends WebApp {
  contentSafeAreaInset?: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
}

interface IProfile {
  user: {
    id: number;
    username: string;
    name: string;
    photo_url: string;
  };
  inventory: {
    items: {
      currency: string;
      amount: number;
      id: number;
      name: string;
      price: number;
      image: string;
    }[];
  };
}

interface IInventoryItem {
  currency: string;
  amount: number;
  id: number;
  name: string;
  price: number;
  image: string;
}

const ProfilePage = () => {
  const [profileData, setProfileData] = useState<IProfile>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isBackgroundLoaded, setIsBackgroundLoaded] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [displayedItems, setDisplayedItems] = useState<IInventoryItem[]>([]);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [banners, setBanners] = useState([]);

  const setOpen = useModalStore((state) => state.setOpen);

  const setNotification = useNotificationStore(
    (state) => state.setNotification,
  );

  const itemsPerPage = 20;

  const openModal = () => {
    setOpen(true)
  }

  const open = useModalStore((state) => state.open)

  const handleWithdraw = async (giftId: number) => {
    const tg = window.Telegram?.WebApp.initData;
    if (tg) {
      try {
        const updatedProfile = await withdrawGift(tg, giftId);
        setProfileData(updatedProfile);
        setNotification({
          type: 'Success',
          message: 'Успешно',
        });
      } catch (e) {
        setNotification({
          type: 'Error',
          message:
            'Чтобы вывести подарок, напишите нам в телеграм канал по кнопке внизу экрана',
        });
        console.error(e);
      }
    }
  };

  useEffect(() => {
    const tg = window.Telegram?.WebApp as ExtendedWebApp;
    if (!tg) return;

    const apply = () => {
      const top = tg?.contentSafeAreaInset?.top ?? 0;
      document.documentElement.style.setProperty(
        '--tg-viewport-content-safe-area-inset-top',
        `${top}px`,
      );
    };

    apply();
    tg.onEvent?.('contentSafeAreaChanged', apply);
    return () => tg.offEvent?.('contentSafeAreaChanged', apply);
  }, []);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const tg = window.Telegram?.WebApp.initData;
      if (tg) {
        const data = await getProfile(tg);
        setProfileData(data);
      }
      setIsLoading(false);
    })();
  }, []);

  useEffect(() => {
    console.log('Начинаем загрузку фонового изображения');
    const img = new Image();
    img.onload = () => {
      console.log('Фоновое изображение загружено успешно');
      setIsBackgroundLoaded(true);
    };
    img.onerror = () => {
      console.log('Ошибка загрузки фонового изображения, продолжаем без него');
      setIsBackgroundLoaded(true);
    };

    img.src = '@/assets/profileIcons/giftItemBackground.webp';

    const timeout = setTimeout(() => {
      console.log('Таймаут загрузки фонового изображения');
      setIsBackgroundLoaded(true);
    }, 10000);

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    if (profileData && profileData.inventory.items) {
      setDisplayedItems(profileData.inventory.items.slice(0, itemsPerPage));
      setHasMore(profileData.inventory.items.length > itemsPerPage);
      setPage(1);
    }
  }, [profileData]);

  const loadMoreItems = () => {
    if (profileData) {
      const nextPage = page + 1;
      const newItems = profileData.inventory.items.slice(
        0,
        nextPage * itemsPerPage,
      );
      setDisplayedItems(newItems);
      setPage(nextPage);
      setHasMore(newItems.length < profileData.inventory.items.length);
    }
  };

  const isAllLoaded = isBackgroundLoaded && !isLoading;

  useEffect(() => {
    if (!isAllLoaded) {
      const timer = setTimeout(() => {
        setShowLoader(true);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setShowLoader(false);
    }
  }, [isAllLoaded]);

  return (
    <div className={styles.container}>
      {/* <ProfileHeader /> */}
      <BalanceTop/>
      <div className={styles.container__contentContainer}>
        {!isAllLoaded && (
          <div className={styles.container__loaderContainer}>
            <CustomLoader />
          </div>
        )}

        <div style={{ visibility: isAllLoaded ? 'visible' : 'hidden' }}>
          <div className={styles.contentContainer}>
            <UserInfoTop />
            <div className={styles.inventoryBanner}>
              <div className={styles.inventoryBanner__text}>
                <div className={styles.inventoryBanner__header}>
                  Collection
                </div>
                <div className={styles.inventoryBanner__p}>
                  <span className={styles.inventoryBanner__icon}><StarIcon/></span>Собирай осколки <br/>и получай реальные<br/> подарки!
                </div>
              </div>
              <div className={styles.inventoryBanner__icons}>
                <div className={styles.inventoryBanner__vector}><VectorIcon/></div>
                <div className={styles.inventoryBanner__background}></div>
              </div>
            </div>
            <div className={styles.inventoryHeaderContainer}>
              <p>Твоя коллекция</p>
              <div className={styles.merge}>
                <StarIcon/>
                <div onClick={openModal} className={styles.merge__text}>Слияние осколков</div>
              </div>
            </div>
            {/* <BalanceTop /> */}
            {/* <ProfileMenu /> */}
            {/* {profileData?.inventory.items &&
            profileData.inventory.items.length > 0 ? (
              <HaveInventory
                inventory={{ items: displayedItems }}
                loadMore={loadMoreItems}
                hasMore={hasMore}
                onWithdraw={handleWithdraw}
              />
            ) : (
              <NoInventory />
            )} */}
            <Inventory />
            {open && <GiftModal/>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
